import {
  brandOptions,
  organizations,
  suppliers,
  contactPersons,
  adminUnits,
} from './itemsFixtureData';

/**
 * Deterministic probes for the bulk-edit UPDATE EXECUTION epic (DL-62328): multi-field value
 * scenarios, mixed/same/partial initial values, feedback (toast/table refresh), and field
 * validation (CF date format, mandatory-for-export). Isolable via `?search=BulkUpdateProbe`
 * (unique substring, absent from every generated/other fixture name), with a sub-word per
 * scenario for further narrowing when a test only wants its own rows (e.g.
 * `?search=BulkUpdateProbe Multi`).
 *
 * Split out of `items.ts` — same reason `itemsFixtureData.ts` was: keeps the handler file under
 * the repo's 800-line cap (`.eslintrc.js` `max-lines`). Base shape mirrors
 * `bulkEditProbeSupplierEmptyA` (`items.ts`); every field an individual test cares about is set
 * explicitly on that scenario's items rather than inherited, so a shared-base edit later can't
 * silently change what a test asserts.
 */
const bulkUpdateProbeBase = {
  organization: organizations[0],
  supplier: suppliers[0],
  companyContactPerson: contactPersons[0],
  supplierContactPerson: null,
  logo: null,
  suppliedBy: null,
  readyForExport: false,
  updatedAfterLastExport: false,
  lastUpdated: '2026-01-15T09:00:00',
  deadline: null,
  etd: null,
  erpItemId: null,
  nameGeneratorExtraText: null,
  physicalReferenceNumber: null,
  categories: [],
  customFields: [],
};

/** Testmo #1809249 (DL-62352) — multiple field updates; also reused by the shared API-failure
 *  test. Each item carries a DIFFERENT description/defaultQty so the adversarial "untouched
 *  fields are not collapsed" assertion has something item-specific to check post-update. */
export const bulkUpdateProbeMultiA = {
  ...bulkUpdateProbeBase,
  id: 90230,
  name: 'BulkUpdateProbe Multi A',
  userDefinedId: 'IT-90230',
  description: 'Multi A original description',
  brand: brandOptions[0],
  adminUnit: adminUnits[0],
  isActive: false,
  defaultQty: 3,
};
export const bulkUpdateProbeMultiB = {
  ...bulkUpdateProbeMultiA,
  id: 90231,
  name: 'BulkUpdateProbe Multi B',
  userDefinedId: 'IT-90231',
  description: 'Multi B original description',
  defaultQty: 4,
};
export const bulkUpdateProbeMultiC = {
  ...bulkUpdateProbeMultiA,
  id: 90232,
  name: 'BulkUpdateProbe Multi C',
  userDefinedId: 'IT-90232',
  description: 'Multi C original description',
  defaultQty: 5,
};

/** Testmo #1809250 (DL-62353) — mixed initial Brand values (3 different brands). */
export const bulkUpdateProbeMixedA = {
  ...bulkUpdateProbeBase,
  id: 90233,
  name: 'BulkUpdateProbe Mixed A',
  userDefinedId: 'IT-90233',
  description: null,
  brand: brandOptions[0],
  adminUnit: adminUnits[0],
  isActive: true,
  defaultQty: 1,
};
export const bulkUpdateProbeMixedB = {
  ...bulkUpdateProbeMixedA,
  id: 90234,
  name: 'BulkUpdateProbe Mixed B',
  userDefinedId: 'IT-90234',
  brand: brandOptions[1 % brandOptions.length],
};
export const bulkUpdateProbeMixedC = {
  ...bulkUpdateProbeMixedA,
  id: 90235,
  name: 'BulkUpdateProbe Mixed C',
  userDefinedId: 'IT-90235',
  brand: brandOptions[2 % brandOptions.length],
};

/** Testmo #1809251 (DL-62354) — same initial Brand value (4 items, all `brandOptions[0]`). */
export const bulkUpdateProbeSameA = {
  ...bulkUpdateProbeBase,
  id: 90236,
  name: 'BulkUpdateProbe Same A',
  userDefinedId: 'IT-90236',
  description: null,
  brand: brandOptions[0],
  adminUnit: adminUnits[0],
  isActive: true,
  defaultQty: 1,
};
export const bulkUpdateProbeSameB = {
  ...bulkUpdateProbeSameA,
  id: 90237,
  name: 'BulkUpdateProbe Same B',
  userDefinedId: 'IT-90237',
};
export const bulkUpdateProbeSameC = {
  ...bulkUpdateProbeSameA,
  id: 90238,
  name: 'BulkUpdateProbe Same C',
  userDefinedId: 'IT-90238',
};
export const bulkUpdateProbeSameD = {
  ...bulkUpdateProbeSameA,
  id: 90239,
  name: 'BulkUpdateProbe Same D',
  userDefinedId: 'IT-90239',
};

/** Testmo #1809252 (DL-62355) — partial update: 3 items with varied Status/Unit/Quantity so
 *  "only Brand changes" has non-uniform, non-Brand fields to prove untouched. */
export const bulkUpdateProbePartialA = {
  ...bulkUpdateProbeBase,
  id: 90240,
  name: 'BulkUpdateProbe Partial A',
  userDefinedId: 'IT-90240',
  description: 'Partial A original description',
  brand: brandOptions[0],
  adminUnit: adminUnits[0],
  isActive: true,
  defaultQty: 2,
};
export const bulkUpdateProbePartialB = {
  ...bulkUpdateProbePartialA,
  id: 90241,
  name: 'BulkUpdateProbe Partial B',
  userDefinedId: 'IT-90241',
  description: 'Partial B original description',
  adminUnit: adminUnits[1 % adminUnits.length],
  isActive: false,
  defaultQty: 7,
};
export const bulkUpdateProbePartialC = {
  ...bulkUpdateProbePartialA,
  id: 90242,
  name: 'BulkUpdateProbe Partial C',
  userDefinedId: 'IT-90242',
  description: 'Partial C original description',
  adminUnit: adminUnits[2 % adminUnits.length],
  isActive: true,
  defaultQty: 11,
};

/** Feedback epic (Testmo #1809253/#1809254, DL-62356/DL-62357) — toast count + table refresh.
 *  5 items so the "5 items updated" / "2 items updated" size variants both have enough rows;
 *  `isActive: true` (all Active) so an `isActive=1` URL filter starts non-empty and one edited
 *  row (flipped to Inactive) provably drops out of that filtered view on refresh. */
export const bulkUpdateProbeFeedbackA = {
  ...bulkUpdateProbeBase,
  id: 90243,
  name: 'BulkUpdateProbe Feedback A',
  userDefinedId: 'IT-90243',
  description: null,
  brand: brandOptions[0],
  adminUnit: adminUnits[0],
  isActive: true,
  defaultQty: 1,
};
export const bulkUpdateProbeFeedbackB = {
  ...bulkUpdateProbeFeedbackA,
  id: 90244,
  name: 'BulkUpdateProbe Feedback B',
  userDefinedId: 'IT-90244',
};
export const bulkUpdateProbeFeedbackC = {
  ...bulkUpdateProbeFeedbackA,
  id: 90245,
  name: 'BulkUpdateProbe Feedback C',
  userDefinedId: 'IT-90245',
};
export const bulkUpdateProbeFeedbackD = {
  ...bulkUpdateProbeFeedbackA,
  id: 90246,
  name: 'BulkUpdateProbe Feedback D',
  userDefinedId: 'IT-90246',
};
export const bulkUpdateProbeFeedbackE = {
  ...bulkUpdateProbeFeedbackA,
  id: 90247,
  name: 'BulkUpdateProbe Feedback E',
  userDefinedId: 'IT-90247',
};

/** Validation epic (Testmo #1577228, DL-62359) — CF Date ("Launch date", `cf_11_date`) format.
 *  Two items with identical initial details (same brand/unit/status/qty, both with no Launch
 *  date value set) — this is a BULK edit, so the scenario is exercised with a real multi-item
 *  selection rather than a single item, matching every other scenario in this epic. */
export const bulkUpdateProbeDateA = {
  ...bulkUpdateProbeBase,
  id: 90248,
  name: 'BulkUpdateProbe Date A',
  userDefinedId: 'IT-90248',
  description: null,
  brand: brandOptions[0],
  adminUnit: adminUnits[0],
  isActive: true,
  defaultQty: 1,
};
export const bulkUpdateProbeDateB = {
  ...bulkUpdateProbeDateA,
  id: 90251,
  name: 'BulkUpdateProbe Date B',
  userDefinedId: 'IT-90251',
};

/**
 * Value entry shape for a Text-type (`type: 2`) custom field on an item — matches
 * `CustomFieldValue` (`features/misc/table/components/customFields/types.ts`) closely enough for
 * mock purposes. Declared locally (not derived from `items.ts`'s `generateCustomFields`, whose
 * return type is narrowed to the "Season used" AllowedValue shape) to avoid a circular import —
 * `items.ts` imports fixture *data* from this file, not the other way around.
 */
type TextCustomFieldValue = {
  id: number;
  customFieldId: number;
  name: string;
  userDefinedId: string | null;
  type: number;
  value: string;
  itemCustomFieldAllowedValueId: number | null;
  customFieldAllowedValueUserDefinedID: string | null;
  isMandatory: boolean;
  isSupplierEditable: boolean;
  internalOnly: boolean;
  maxChar: number | null;
  isMultiLine: boolean;
  allowedDecimals: number;
  isMultipleAllowedValue: boolean;
  parentCustomFieldId: number | null;
  parentCustomFieldName: string | null;
  allowedValues: unknown[];
};

/**
 * The extra mandatory-for-export CF definition (`customFieldId: 90`, "Export Note") that
 * `bulkUpdateProbeExportReadyA`/`B`/`bulkUpdateProbeExportNotReadyA`/`B` below carry a value for. Not part
 * of `itemCustomFieldDefinitions` (none of those are `isMandatory: true`) — Testmo #1577251 /
 * DL-62360's spec layers this on top via
 * `apiTransform({ url: '/api/meta/items/customfields', data: { customFieldDefinitions: [
 * ...itemCustomFieldDefinitions, mandatoryExportCustomFieldDefinition ] } })` so every other
 * bulk-edit CF column stays available alongside it.
 */
export const mandatoryExportCustomFieldDefinition = {
  customFieldId: 90,
  name: 'Export Note',
  userDefinedId: 'EXPORTNOTE',
  type: 2,
  isMandatory: true,
  isSupplierEditable: true,
  internalOnly: false,
  maxChar: 500,
  isMultiLine: true,
  allowedDecimals: 0,
  isMultipleAllowedValue: false,
  parentCustomFieldId: null,
  parentCustomFieldName: null,
};

const exportNoteCf = (cfEntryId: number, value: string): TextCustomFieldValue => ({
  id: cfEntryId,
  customFieldId: mandatoryExportCustomFieldDefinition.customFieldId,
  name: mandatoryExportCustomFieldDefinition.name,
  userDefinedId: mandatoryExportCustomFieldDefinition.userDefinedId,
  type: mandatoryExportCustomFieldDefinition.type,
  value,
  itemCustomFieldAllowedValueId: null,
  customFieldAllowedValueUserDefinedID: null,
  isMandatory: mandatoryExportCustomFieldDefinition.isMandatory,
  isSupplierEditable: mandatoryExportCustomFieldDefinition.isSupplierEditable,
  internalOnly: mandatoryExportCustomFieldDefinition.internalOnly,
  maxChar: mandatoryExportCustomFieldDefinition.maxChar,
  isMultiLine: mandatoryExportCustomFieldDefinition.isMultiLine,
  allowedDecimals: mandatoryExportCustomFieldDefinition.allowedDecimals,
  isMultipleAllowedValue: mandatoryExportCustomFieldDefinition.isMultipleAllowedValue,
  parentCustomFieldId: mandatoryExportCustomFieldDefinition.parentCustomFieldId,
  parentCustomFieldName: mandatoryExportCustomFieldDefinition.parentCustomFieldName,
  allowedValues: [],
});

/**
 * Validation epic (Testmo #1577251, DL-62360) — mandatory-for-export custom field. Two Ready
 * items and two Not-Ready items — same mandatory field, same "same details" initial value within
 * each pair, and populated on all four so "clear it" is actually reachable from the UI (the
 * field can't be cleared if it's already empty). Two items per group (not one) so this bulk-edit
 * scenario is genuinely exercised across a multi-item selection, matching every other scenario in
 * this epic.
 */
export const bulkUpdateProbeExportReadyA = {
  ...bulkUpdateProbeBase,
  id: 90249,
  name: 'BulkUpdateProbe Export Ready A',
  userDefinedId: 'IT-90249',
  description: null,
  brand: brandOptions[0],
  adminUnit: adminUnits[0],
  isActive: true,
  defaultQty: 1,
  readyForExport: true,
  customFields: [exportNoteCf(90249090, 'Ready note')],
};
export const bulkUpdateProbeExportReadyB = {
  ...bulkUpdateProbeExportReadyA,
  id: 90252,
  name: 'BulkUpdateProbe Export Ready B',
  userDefinedId: 'IT-90252',
  customFields: [exportNoteCf(90252090, 'Ready note')],
};
export const bulkUpdateProbeExportNotReadyA = {
  ...bulkUpdateProbeExportReadyA,
  id: 90250,
  name: 'BulkUpdateProbe Export Not Ready A',
  userDefinedId: 'IT-90250',
  readyForExport: false,
  customFields: [exportNoteCf(90250090, 'Not ready note')],
};
export const bulkUpdateProbeExportNotReadyB = {
  ...bulkUpdateProbeExportNotReadyA,
  id: 90253,
  name: 'BulkUpdateProbe Export Not Ready B',
  userDefinedId: 'IT-90253',
  customFields: [exportNoteCf(90253090, 'Not ready note')],
};
